import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { Bar, Line, HorizontalBar, Doughnut } from 'react-chartjs-2'
import styles from './index.module.scss'
import { COLORS } from '../../utilities/constants'

function ReportChart ({
  type = 'bar',
  horizontal = false,
  series,
  categories,
  height = 350,
  width = '100%',
  title = '',
  markerColor = COLORS?.PRIMARY,
  stacked = false,
  displayTotal = false
}) {
  const [highlightedIndex, setHighlightedIndex] = useState(null)
  const datasets = series.map((s) => {
    const colors = highlightedIndex !== null
      ? Array(s?.data?.length).fill((s.color || markerColor))
        .map((item, index) => index === highlightedIndex ? (s.color || markerColor) : `${s.color || markerColor}40`)
      : (s.color || markerColor)
    return {
      label: s.name || '',
      data: s.data || [],
      backgroundColor: colors,
      borderColor: colors,
      borderWidth: 2,
      fill: type === 'bar',
      tension: type === 'line' ? 0.2 : 0
    }
  })
  const randomColors = () => {
    const colorKeys = Object.keys(COLORS || {})
    return Array.from({ length: categories?.length }, (item, index) => {
      return COLORS[colorKeys?.at(index)] || `#${Math.floor(Math.random() * 16777215).toString(16)}`
    })
  }

  const data = {
    labels: categories,
    datasets
  }

  const colorsForDoughnut = randomColors()
  const colors = highlightedIndex !== null
    ? colorsForDoughnut
      .map((item, index) => index === highlightedIndex ? item : `${item}40`)
    : colorsForDoughnut

  const doughnutDataset = {
    label: series[0]?.name || '',
    data: series[0]?.data || [],
    backgroundColor: colors
  }

  const doughnutData = {
    labels: categories,
    datasets: [doughnutDataset]
  }

  const optionsForBarAndLine = {
    layout: {
      padding: {
        top: 5,
        bottom: 5,
        left: 10,
        right: 10
      }
    },
    onClick: (e, elements) => {
      if (elements.length > 0) {
        const index = elements[0]._index
        setHighlightedIndex(index === highlightedIndex ? null : index)
      }
    },
    responsive: true,
    cutout: type === 'doughnut' ? '50%' : undefined,
    aspectRatio: type === 'doughnut' ? 1 : undefined,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false, position: type === 'doughnut' ? 'bottom' : 'right' },
      tooltip: {
        callbacks: {
          label: function (context) {
            const val = context.raw
            return typeof val === 'number' ? val.toFixed(2) : val
          }
        }
      },
      title: {
        display: false
      }
    },
    scales: {
      xAxes: [
        {
          stacked,
          ticks: {
            autoSkip: false,
            beginAtZero: true,
            maxRotation: 45,
            minRotation: 0,
            font: {
              size: 12
            }
          }
        }
      ],
      yAxes: [
        {
          stacked,
          ticks: {
            callback: function (value) {
              return horizontal ? value : parseFloat(value).toFixed(2)
            }
          }
        }
      ]
    }
  }

  const optionsForDoughnut = {
    layout: {
      padding: {
        top: 5,
        bottom: 5,
        left: 10,
        right: 10
      }
    },
    onClick: (e, elements) => {
      if (elements.length > 0) {
        const index = elements[0]._index
        setHighlightedIndex(index === highlightedIndex ? null : index)
      }
    },
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1, // Keeps it square
    cutout: '50%', // Size of the donut hole (can go higher for thinner ring)
    tooltips: {
      enabled: true,
      callbacks: {
        label: function (context) {
          const val = doughnutDataset?.data[context?.index]
          const data = doughnutDataset.data
          const total = data.reduce((acc, cur) => acc + cur, 0)
          const percentage = total > 0 ? ((val / total) * 100).toFixed(1) : 0
          const label = categories[context?.index] || ''
          return `${label}: ${val} (${percentage}%)`
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom' // Better placement for doughnut
      },
      title: {
        display: false
      }
    }
  }

  const ChartComponentLoader = () => {
    switch (type) {
      case 'line':
        return <Line data={data} options={optionsForBarAndLine} />
      case 'doughnut':
        return <Doughnut data={doughnutData} options={optionsForDoughnut} />
      case 'bar':
        if (horizontal) return <HorizontalBar data={data} options={optionsForBarAndLine} />
        return <Bar data={data} options={optionsForBarAndLine} />
      default:
        return <Bar data={data} options={optionsForBarAndLine} />
    }
  }

  return (
    <div className={styles.cChartParent}>
      <div className={styles.cChart} style={{ width, height }}>
        { displayTotal && series && series?.at(0)?.data?.length > 0 && <p className={styles.subtitle}>Total: { series?.at(0)?.data?.reduce((acc = 0, a) => acc + a) }</p> }
        <div className={styles.cChartContainer}>
          {ChartComponentLoader()}
        </div>
        <p className={styles.title}>{title}</p>
      </div>
    </div>
  )
}

ReportChart.propTypes = {
  type: PropTypes.string,
  title: PropTypes.string,
  markerColor: PropTypes.string,
  horizontal: PropTypes.bool,
  series: PropTypes.any.isRequired,
  categories: PropTypes.array.isRequired,
  height: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  stacked: PropTypes.bool,
  displayTotal: PropTypes.bool
}

export default ReportChart
